import SectionCard, {
  SectionCardGroup
} from "@/appComponents/Blocks/Composites/SectionCard/SectionCard";
import { ApprovalsSettingsKey, usePlanSettingsContext } from "@/providers/PlanSettingsProvider";
import { appstr } from "@coreapp/utils/appstr";
import { Flex, List, ListItemOption, Switch, Text } from "@theorgchartdev/storybook";
import { useMemo } from "react";
import * as StateLib from "@/Utils/StateLib";

const PLAN_APPROVAL_SETTINGS_DISPLAY: Record<
  ApprovalsSettingsKey,
  {
    label: string;
    description: string;
    showDisableAlert?: boolean;
    disableAlertHeader?: string;
    disableAlertMessage?: string;
  }
> = {
  enablePlanApprovals: {
    label: appstr("Enable plan approvals"),
    description: appstr(
      "When enabled, plans can be submitted for approval and e-mail notifications are sent for status updates."
    ),
    showDisableAlert: true,
    disableAlertHeader: appstr("Disable plan approval?"),
    disableAlertMessage: appstr(
      "Disabling plan approval will hide plan approval flows for all plans created from this base chart. Are you sure you want to disable plan approval for this base chart?"
    )
  }
};

const Approvals = () => {
  const { settings, updateSettings, getClonedSettings } = usePlanSettingsContext();

  const showDisableAlert = (
    key: ApprovalsSettingsKey,
    settingConfig: (typeof PLAN_APPROVAL_SETTINGS_DISPLAY)[ApprovalsSettingsKey],
    currentValue: boolean
  ) => {
    const wsMgr = StateLib.GetWSMgr();
    if (wsMgr) {
      wsMgr.CreateReactAlert(
        settingConfig?.disableAlertHeader ?? "",
        settingConfig?.disableAlertMessage ?? "",
        () => {
          updateSettings({
            approvalsSettings: {
              ...(settings?.approvalsSettings ?? {}),
              [key]: !currentValue
            }
          });
        },
        () => {},
        appstr("Disable"),
        appstr("Cancel")
      );
    }
  };

  const handleToggleChange = (key: ApprovalsSettingsKey) => {
    const currentValue = settings?.approvalsSettings?.[key] ?? false;
    const clonedSettings = getClonedSettings();
    const savedValue = clonedSettings?.approvalsSettings?.[key] ?? false;
    const settingConfig = PLAN_APPROVAL_SETTINGS_DISPLAY[key];

    if (currentValue === true && savedValue === true && settingConfig.showDisableAlert) {
      showDisableAlert(key, settingConfig, currentValue);
    } else {
      updateSettings({
        approvalsSettings: {
          ...(settings?.approvalsSettings ?? {}),
          [key]: !currentValue
        }
      });
    }
  };

  const fieldElements: ListItemOption[] = useMemo(() => {
    return Object.entries(PLAN_APPROVAL_SETTINGS_DISPLAY).map(([key, { label, description }]) => {
      const settingKey = key as ApprovalsSettingsKey;
      return {
        content: (
          <span className="ocn-approval-settings-option">
            <span>
              <Switch
                id={`plan-approval-setting-${settingKey}`}
                onChange={() => handleToggleChange(settingKey)}
                checked={settings?.approvalsSettings?.[settingKey] ?? false}
              />
            </span>
            <Flex direction="column" gap="8px" flex="1">
              <Text modifier="medium">{label}</Text>
              <Text>{description}</Text>
            </Flex>
          </span>
        ),
        clickHandler: (_, e) => {
          e?.preventDefault();
          handleToggleChange(settingKey);
        },
        label: settingKey,
        dataTestId: `plan-approval-setting-${settingKey}`,
        value: settingKey
      };
    });
  }, [settings, handleToggleChange]);

  return (
    <SectionCardGroup>
      <SectionCard
        title={appstr("Approval settings")}
        titleContainerProps={{
          element: "h4",
          modifier: "bold",
          size: "xsmall",
          style: { marginBottom: 0 }
        }}
        headerFlexProps={{
          direction: "column",
          alignItems: "flex-start",
          justifyContent: "unset"
        }}
        flexProps={{
          height: "100%"
        }}
        childWrapperClass={{
          padding: "4px 4px 0",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          flex: "1",
          height: "100%",
          overflow: "hidden"
        }}
      >
        <List
          enableVirtualization={false}
          options={fieldElements}
          listWidth="fill"
          optionSize="medium"
          showCheck={false}
        />
      </SectionCard>
    </SectionCardGroup>
  );
};

export default Approvals;
